import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, TimePicker, Input, Button } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import { RangePickerProps } from "antd/lib/date-picker";
import moment from "moment";
import { DayPicker, DayModifiers } from "react-day-picker";
import "react-day-picker/dist/style.css";
import pt from "date-fns/locale/pt-BR";
import { FieldTimeOutlined } from "@ant-design/icons";
import { lastDayOfMonth, format } from "date-fns";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import { IRequirement } from "../../Project";
import {
  Calendar,
  CalendarContainer,
  Period,
  TotalHours,
  TotalHoursContainer,
} from "./styles";

const { TextArea } = Input;
const { RangePicker } = TimePicker;

interface IScheduledDay {
  id: string;
  date: string;
  task_description: string | undefined | null;
  morning_start_time: string | undefined | null;
  morning_end_time: string | undefined | null;
  afternoon_start_time: string | undefined | null;
  afternoon_end_time: string | undefined | null;
  evening_start_time: string | undefined | null;
  evening_end_time: string | undefined | null;
}

interface IAssignActivityModalProps {
  activity?: IRequirement;
  visible: boolean;
  handleClose: (activity?: IRequirement) => void;
}

const TimeSheetRecordingModal: React.FC<IAssignActivityModalProps> = ({
  activity,
  visible,
  handleClose,
}) => {
  const { t } = useTranslation(["activities-actions"]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedDaySchedule, setSelectedDaySchedule] = useState<
    IScheduledDay
  >();
  const [scheduledDays, setScheduledDays] = useState<IScheduledDay[]>([]);

  const { addToast } = useToast();

  useEffect(() => {
    if (activity) {
      let date = new Date();

      if (activity.month !== date.getMonth()) {
        date = lastDayOfMonth(new Date(activity.year, activity.month, 1));
      }

      setLoading(true);
      api
        .get<IScheduledDay[]>(`/activities/${activity?.id}/schedule`)
        .then((response) => {
          setScheduledDays(response.data);
          setSelectedDate(date);
          setSelectedDaySchedule(
            response.data.find(
              (scheduleDay) =>
                scheduleDay.date === moment(date).format("YYYY-MM-DD")
            )
          );
        })
        .catch(() => {
          addToast({
            type: "error",
            title: "Erro na busca",
            description:
              "Occoreu um erro ao buscar a sua planilha, tente novamente.",
          });
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity]);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 404:
          description = "A atividade não foi encontrada";
          break;
        case 403:
          description =
            "As horas só podem ser apontadas pleo usuário atribuído";
          break;
        default:
          description =
            "Um erro occoreu ao atribuir a atividade, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao apontar horas",
        description,
      });
    },
    [addToast]
  );

  const handleClearAndClose = useCallback(() => {
    setSelectedDaySchedule(undefined);
    setSelectedDate(new Date());
    setScheduledDays([]);
    handleClose();
  }, [handleClose]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    try {
      await api.patch(`/activities/${activity?.id}/schedule`, {
        allocation_days: scheduledDays.map((day) => ({
          id: day.id,
          task_description: day.task_description,
          morning_start_time: day.morning_start_time,
          morning_end_time: day.morning_end_time,
          afternoon_start_time: day.afternoon_start_time,
          afternoon_end_time: day.afternoon_end_time,
          evening_start_time: day.evening_start_time,
          evening_end_time: day.evening_end_time,
        })),
      });

      handleClearAndClose();

      addToast({
        type: "success",
        title: "Horas apontadas",
        description: "As horas foram apontadas com sucesso",
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    }

    setLoading(false);
  }, [
    activity?.id,
    addToast,
    generateErrorToast,
    handleClearAndClose,
    scheduledDays,
  ]);

  const handleDateChange = useCallback(
    (day: Date, modifiers: DayModifiers) => {
      if (day && !modifiers.disabled) {
        setSelectedDate(day);
        setSelectedDaySchedule(
          scheduledDays.find(
            (scheduleDay) =>
              scheduleDay.date === moment(day).format("YYYY-MM-DD")
          )
        );
      }
    },
    [scheduledDays]
  );

  const filledDays = useMemo(
    () =>
      scheduledDays
        .filter(
          (day) =>
            !!day.morning_start_time &&
            !!day.morning_end_time &&
            !!day.afternoon_start_time &&
            !!day.afternoon_end_time &&
            !!day.task_description
        )
        .map((day) => moment(day.date, "YYYY-MM-DD").toDate()),
    [scheduledDays]
  );

  const morningFilledDays = useMemo(
    () =>
      scheduledDays
        .filter(
          (day) =>
            !!day.morning_start_time &&
            !!day.morning_end_time &&
            !!day.task_description
        )
        .map((day) => moment(day.date, "YYYY-MM-DD").toDate()),
    [scheduledDays]
  );

  const afternoonFilledDays = useMemo(
    () =>
      scheduledDays
        .filter(
          (day) =>
            !!day.afternoon_start_time &&
            !!day.afternoon_end_time &&
            !!day.task_description
        )
        .map((day) => moment(day.date, "YYYY-MM-DD").toDate()),
    [scheduledDays]
  );

  const onChangeMorningTime: RangePickerProps["onChange"] = useCallback(
    (dates, _) => {
      if (dates) {
        setScheduledDays((old) => {
          const newArray = old;
          const index = newArray.findIndex(
            (d) => d.id === selectedDaySchedule?.id
          );
          newArray[index].morning_start_time = dates[0].format("HH:mm:ss");
          newArray[index].morning_end_time = dates[1].format("HH:mm:ss");
          return [...newArray];
        });
      } else {
        setScheduledDays((old) => {
          const newArray = old;
          const index = newArray.findIndex(
            (d) => d.id === selectedDaySchedule?.id
          );
          newArray[index].morning_start_time = null;
          newArray[index].morning_end_time = null;
          return [...newArray];
        });
      }
    },
    [selectedDaySchedule?.id]
  );

  const onChangeAfternoonTime: RangePickerProps["onChange"] = useCallback(
    (dates, _) => {
      if (dates) {
        setScheduledDays((old) => {
          const newArray = old;
          const index = newArray.findIndex(
            (d) => d.id === selectedDaySchedule?.id
          );
          newArray[index].afternoon_start_time = dates[0].format("HH:mm:ss");
          newArray[index].afternoon_end_time = dates[1].format("HH:mm:ss");
          return [...newArray];
        });
      } else {
        setScheduledDays((old) => {
          const newArray = old;
          const index = newArray.findIndex(
            (d) => d.id === selectedDaySchedule?.id
          );
          newArray[index].afternoon_start_time = null;
          newArray[index].afternoon_end_time = null;
          return [...newArray];
        });
      }
    },
    [selectedDaySchedule?.id]
  );

  const onChangeEveningTime: RangePickerProps["onChange"] = useCallback(
    (dates, _) => {
      if (dates) {
        setScheduledDays((old) => {
          const newArray = old;
          const index = newArray.findIndex(
            (d) => d.id === selectedDaySchedule?.id
          );
          newArray[index].evening_start_time = dates[0].format("HH:mm:ss");
          newArray[index].evening_end_time = dates[1].format("HH:mm:ss");
          return [...newArray];
        });
      } else {
        setScheduledDays((old) => {
          const newArray = old;
          const index = newArray.findIndex(
            (d) => d.id === selectedDaySchedule?.id
          );
          newArray[index].evening_start_time = null;
          newArray[index].evening_end_time = null;
          return [...newArray];
        });
      }
    },
    [selectedDaySchedule?.id]
  );

  const onChangeTaskDescription = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setScheduledDays((old) => {
        const newArray = old;
        const index = newArray.findIndex(
          (d) => d.id === selectedDaySchedule?.id
        );
        newArray[index].task_description =
          e.target.value === "" ? null : e.target.value;
        return [...newArray];
      });
    },
    [selectedDaySchedule?.id]
  );

  const totalHours = useMemo(
    () =>
      scheduledDays.reduce((previousValue, currentValue) => {
        let morning = 0;
        let afternoon = 0;
        let evening = 0;

        if (currentValue.morning_start_time && currentValue.morning_end_time) {
          morning = moment(currentValue.morning_end_time, "HH:mm:ss").diff(
            moment(currentValue.morning_start_time, "HH:mm:ss"),
            "hours",
            true
          );
        }
        if (
          currentValue.afternoon_start_time &&
          currentValue.afternoon_end_time
        ) {
          afternoon = moment(currentValue.afternoon_end_time, "HH:mm:ss").diff(
            moment(currentValue.afternoon_start_time, "HH:mm:ss"),
            "hours",
            true
          );
        }
        if (currentValue.evening_start_time && currentValue.evening_end_time) {
          evening = moment(currentValue.evening_end_time, "HH:mm:ss").diff(
            moment(currentValue.evening_start_time, "HH:mm:ss"),
            "hours",
            true
          );
        }

        return previousValue + morning + afternoon + evening;
      }, 0),
    [scheduledDays]
  );

  const totalHoursFormatted = useMemo(() => {
    const rhours = Math.floor(totalHours);
    const minutes = (totalHours - rhours) * 60;
    const rminutes = Math.round(minutes);

    return `${rhours}h${rminutes}`;
  }, [totalHours]);

  const handleFillStandardTime = useCallback(() => {
    setScheduledDays((old) => {
      const newArray = old;
      const index = newArray.findIndex((d) => d.id === selectedDaySchedule?.id);
      newArray[index].morning_start_time = "08:00:00";
      newArray[index].morning_end_time = "12:00:00";
      newArray[index].afternoon_start_time = "14:00:00";
      newArray[index].afternoon_end_time = "18:00:00";
      return [...newArray];
    });
  }, [selectedDaySchedule?.id]);

  return (
    <Modal
      title={t("record-time-sheet")}
      visible={visible}
      onOk={handleSubmit}
      width={740}
      onCancel={() => handleClearAndClose()}
      okButtonProps={{
        loading,
      }}
      getContainer={false}
      destroyOnClose
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CalendarContainer>
        <div style={{ flex: 1, paddingRight: 30 }}>
          <Period>
            <h4>Data:</h4>
            <h3>{moment(selectedDate).format("dddd DD [de] MMMM")}</h3>
          </Period>

          <Period>
            <h4>Manhã:</h4>
            <RangePicker
              style={{ width: 240 }}
              format="HH:mm"
              onChange={onChangeMorningTime}
              value={
                selectedDaySchedule?.morning_start_time &&
                selectedDaySchedule.morning_end_time
                  ? [
                      moment(
                        selectedDaySchedule?.morning_start_time,
                        "HH:mm:ss"
                      ),
                      moment(selectedDaySchedule?.morning_end_time, "HH:mm:ss"),
                    ]
                  : undefined
              }
            />
          </Period>
          <Period>
            <h4>Tarde:</h4>
            <RangePicker
              style={{ width: 240 }}
              format="HH:mm"
              onChange={onChangeAfternoonTime}
              value={
                selectedDaySchedule?.afternoon_start_time &&
                selectedDaySchedule.afternoon_end_time
                  ? [
                      moment(
                        selectedDaySchedule?.afternoon_start_time,
                        "HH:mm:ss"
                      ),
                      moment(
                        selectedDaySchedule?.afternoon_end_time,
                        "HH:mm:ss"
                      ),
                    ]
                  : undefined
              }
            />
          </Period>
          <Period>
            <h4>Noite:</h4>
            <RangePicker
              style={{ width: 240 }}
              format="HH:mm"
              onChange={onChangeEveningTime}
              value={
                selectedDaySchedule?.evening_start_time &&
                selectedDaySchedule.evening_end_time
                  ? [
                      moment(
                        selectedDaySchedule?.evening_start_time,
                        "HH:mm:ss"
                      ),
                      moment(selectedDaySchedule?.evening_end_time, "HH:mm:ss"),
                    ]
                  : undefined
              }
            />
          </Period>
          <Period>
            <h4>Atividades:</h4>
            <TextArea
              style={{ width: 240 }}
              rows={6}
              spellCheck={false}
              onChange={onChangeTaskDescription}
              value={selectedDaySchedule?.task_description || ""}
            />
          </Period>
          <Button
            style={{ padding: 0 }}
            type="link"
            onClick={handleFillStandardTime}
          >
            <FieldTimeOutlined style={{ fontSize: 16 }} />
            {t("fill_standard_time")}
          </Button>
        </div>
        <Calendar>
          <DayPicker
            locale={pt}
            disabled={[{ dayOfWeek: [0, 6] }]}
            modifiers={{
              morningFilled: morningFilledDays,
              afternoonFilled: afternoonFilledDays,
              filled: filledDays,
            }}
            modifiersClassNames={{
              selected: "selected-day",
              filled: "filled-days",
              morningFilled: "morning-filled-days",
              afternoonFilled: "afternoon-filled-days",
            }}
            disableNavigation
            selected={selectedDate}
            onDayClick={handleDateChange}
            month={selectedDate}
          />
          <TotalHoursContainer>
            Horas apontadas:<TotalHours>{totalHoursFormatted}</TotalHours>
          </TotalHoursContainer>
        </Calendar>
      </CalendarContainer>
    </Modal>
  );
};

export default TimeSheetRecordingModal;
